import { useThemeMode } from '@/contexts/ThemeContext'
import { getAvailableProviders } from '@/lib/ai-providers'
import Container from '../ui/Container'
import { useAIProviderSelector } from './AIProviderSelector'
import { ChatInterface } from './ChatInterface'

interface ChatApplicationContentProps {
  fontMode: 'departure-mono' | 'open-dyslexic' | 'system'
  onWebSearchStateChange?: (state: boolean, toggleHandler: () => void) => void
  onTextSizeStateChange?: (size: number, handler: (size: number) => void) => void
  onWalletStateChange?: (hasWallet: boolean) => void
  onGuestModeStateChange?: (isGuestMode: boolean) => void
  onUsageLimitReached?: (resetTime: string) => void
  onOpenSubscription?: () => void
}

function ChatApplicationContent({
  fontMode,
  onWebSearchStateChange,
  onTextSizeStateChange,
  onWalletStateChange,
  onGuestModeStateChange,
  onUsageLimitReached,
  onOpenSubscription,
}: ChatApplicationContentProps) {
  const availableProviders = getAvailableProviders()
  const { colors } = useThemeMode()

  // AI Provider state management
  const {
    selectedProvider,
    selectedModel,
    availableModels,
    isLoadingModels,
    modelError,
    handleProviderChange,
    handleModelChange,
  } = useAIProviderSelector({
    onProviderChange: () => {},
    onModelChange: () => {},
    onProviderAndModelReady: () => {},
  })

  // Get font family based on fontMode
  const fontFamily =
    fontMode === 'departure-mono'
      ? 'var(--font-departure-mono)'
      : fontMode === 'open-dyslexic'
        ? 'var(--font-open-dyslexic)'
        : 'inherit'

  return (
    <Container
      variant="secondary"
      padding="none"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        backgroundColor: colors.primaryBg,
        fontFamily,
      }}
    >
      <ChatInterface
        selectedProvider={selectedProvider}
        selectedModel={selectedModel}
        availableModels={availableModels}
        isLoadingModels={isLoadingModels}
        modelError={modelError}
        availableProviders={availableProviders}
        onProviderChange={handleProviderChange}
        onModelChange={handleModelChange}
        fontMode={fontMode}
        onWebSearchStateChange={onWebSearchStateChange}
        onTextSizeStateChange={onTextSizeStateChange}
        onWalletStateChange={onWalletStateChange}
        onGuestModeStateChange={onGuestModeStateChange}
        onUsageLimitReached={onUsageLimitReached}
        onOpenSubscription={onOpenSubscription}
      />
    </Container>
  )
}

interface ChatApplicationProps {
  fontMode?: 'departure-mono' | 'open-dyslexic' | 'system'
  onWebSearchStateChange?: (state: boolean, toggleHandler: () => void) => void
  onTextSizeStateChange?: (size: number, handler: (size: number) => void) => void
  onWalletStateChange?: (hasWallet: boolean) => void
  onGuestModeStateChange?: (isGuestMode: boolean) => void
  onUsageLimitReached?: (resetTime: string) => void
  onOpenSubscription?: () => void
  isLoadingSubscription?: boolean
}

export default function ChatApplication({
  fontMode = 'departure-mono',
  onWebSearchStateChange,
  onTextSizeStateChange,
  onWalletStateChange,
  onGuestModeStateChange,
  onUsageLimitReached,
  onOpenSubscription,
  isLoadingSubscription = false,
}: ChatApplicationProps) {
  return (
    <ChatApplicationContent
      fontMode={fontMode}
      onWebSearchStateChange={onWebSearchStateChange}
      onTextSizeStateChange={onTextSizeStateChange}
      onWalletStateChange={onWalletStateChange}
      onGuestModeStateChange={onGuestModeStateChange}
      onUsageLimitReached={onUsageLimitReached}
      onOpenSubscription={onOpenSubscription}
    />
  )
}
